/**
 * 视频陈列模块 JavaScript
 * Video Gallery Section Script
 *
 * 功能：
 * 1. 缩略图懒加载（Intersection Observer）
 * 2. 视频模态框控制
 * 3. 分页/加载更多
 * 4. 键盘导航支持
 * 5. YouTube iframe API集成
 */

(function () {
  'use strict';

  // ==================== 工具函数 ====================

  /**
   * 从URL提取YouTube视频ID
   * @param {string} url - YouTube URL
   * @returns {string|null} - 视频ID
   */
  function extractYouTubeId(url) {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  /**
   * 格式化数字（如 12500 -> 12.5K）
   * @param {number} num - 数字
   * @returns {string} - 格式化后的字符串
   */
  function formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
  }

  /**
   * 防抖函数
   * @param {Function} func - 要防抖的函数
   * @param {number} wait - 等待时间
   * @returns {Function}
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // ==================== 视频陈列模块类 ====================

  class VideoGallery {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.section = document.getElementById(`video-gallery-${sectionId}`);
      this.modal = document.getElementById(`vg-modal-${sectionId}`);
      this.iframe = document.getElementById(`vg-iframe-${sectionId}`);
      this.dataScript = document.getElementById(`vg-data-${sectionId}`);

      if (!this.section || !this.dataScript) {
        console.warn(`Video Gallery: Section ${sectionId} not found`);
        return;
      }

      // 解析视频数据
      try {
        this.data = JSON.parse(this.dataScript.textContent);
      } catch (e) {
        console.error('Video Gallery: Failed to parse video data', e);
        this.data = { videos: [], settings: {} };
      }

      this.videos = this.data.videos || [];
      this.settings = this.data.settings || {};
      this.currentPage = 1;
      this.videosPerPage = this.settings.videosPerPage || 8;
      this.currentVideoIndex = null;
      this.isMobile = window.innerWidth < 768;

      this.init();
    }

    init() {
      this.initLazyLoad();
      this.initModal();
      this.initPagination();
      this.initKeyboardNav();
      this.initResizeListener();

      // 初始隐藏超出第一页的视频
      this.updateVisibleVideos();
    }

    // ==================== 懒加载 ====================

    initLazyLoad() {
      const lazyImages = this.section.querySelectorAll('.vg-thumbnail.lazy');

      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const img = entry.target;
                this.loadImage(img);
                observer.unobserve(img);
              }
            });
          },
          {
            rootMargin: '100px 0px',
            threshold: 0.1
          }
        );

        lazyImages.forEach((img) => imageObserver.observe(img));
      } else {
        // 降级处理
        lazyImages.forEach((img) => this.loadImage(img));
      }
    }

    loadImage(img) {
      const src = img.dataset.src;
      if (src) {
        img.src = src;
        img.classList.remove('lazy');
        img.addEventListener('load', () => {
          img.style.opacity = '1';
        });
        img.addEventListener('error', () => {
          // 加载失败时使用默认图片
          img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E';
        });
      }
    }

    // ==================== 模态框 ====================

    initModal() {
      const cards = this.section.querySelectorAll('.vg-card');
      const closeBtn = this.modal.querySelector('.vg-modal-close');

      // 点击卡片打开模态框
      cards.forEach((card, index) => {
        card.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal(index);
        });

        // 键盘支持
        card.setAttribute('tabindex', '0');
        card.setAttribute('role', 'button');
        card.setAttribute('aria-label', 'Play video');
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.openModal(index);
          }
        });
      });

      // 关闭按钮
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeModal());
      }

      // 点击背景关闭
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });

      // 分享按钮
      const shareBtn = this.modal.querySelector('.vg-share-btn');
      if (shareBtn) {
        shareBtn.addEventListener('click', () => this.handleShare());
      }
    }

    openModal(index) {
      const video = this.videos[index];
      if (!video) return;

      this.currentVideoIndex = index;

      // 设置iframe源（添加autoplay）
      const autoplay = this.settings.autoplay ? 1 : 0;
      this.iframe.src = `https://www.youtube.com/embed/${video.videoId}?autoplay=${autoplay}&rel=0&modestbranding=1`;

      // 设置视频信息
      const info = this.modal.querySelector('.vg-modal-info');
      const date = this.modal.querySelector('.vg-modal-date');
      const description = this.modal.querySelector('.vg-modal-description');
      const likesCount = this.modal.querySelector('.vg-likes-count');
      const commentsCount = this.modal.querySelector('.vg-comments-count');

      if (date) date.textContent = video.date || '';
      if (description) description.textContent = video.description || '';
      if (likesCount) likesCount.textContent = video.likes || '0';
      if (commentsCount) commentsCount.textContent = video.comments || '0';

      // 移动端显示叠加信息
      if (this.isMobile && info) {
        info.classList.add('mobile-visible');
      }

      // 显示模态框
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // 焦点管理
      const closeBtn = this.modal.querySelector('.vg-modal-close');
      if (closeBtn) closeBtn.focus();
    }

    closeModal() {
      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // 停止视频
      this.iframe.src = '';

      // 移除移动端叠加信息
      const info = this.modal.querySelector('.vg-modal-info');
      if (info) {
        info.classList.remove('mobile-visible');
      }

      // 恢复焦点到触发元素
      if (this.currentVideoIndex !== null) {
        const cards = this.section.querySelectorAll('.vg-card');
        if (cards[this.currentVideoIndex]) {
          cards[this.currentVideoIndex].focus();
        }
      }

      this.currentVideoIndex = null;
    }

    // ==================== 分页 ====================

    initPagination() {
      const loadMoreBtn = this.section.querySelector('.vg-load-btn');
      if (!loadMoreBtn) return;

      loadMoreBtn.addEventListener('click', () => {
        this.currentPage++;
        this.updateVisibleVideos();
        this.initLazyLoad(); // 重新初始化懒加载
      });
    }

    updateVisibleVideos() {
      const cards = this.section.querySelectorAll('.vg-card');
      const totalVideos = cards.length;
      const visibleCount = this.currentPage * this.videosPerPage;

      cards.forEach((card, index) => {
        if (index < visibleCount) {
          card.dataset.hidden = 'false';
          card.style.display = '';
        } else {
          card.dataset.hidden = 'true';
          card.style.display = 'none';
        }
      });

      // 更新加载更多按钮状态
      const loadMoreContainer = this.section.querySelector('.vg-load-more');
      if (loadMoreContainer) {
        loadMoreContainer.style.display = visibleCount >= totalVideos ? 'none' : 'flex';
      }
    }

    // ==================== 键盘导航 ====================

    initKeyboardNav() {
      document.addEventListener('keydown', (e) => {
        if (!this.modal.classList.contains('active')) return;

        switch (e.key) {
          case 'Escape':
            this.closeModal();
            break;
          case 'ArrowLeft':
            this.navigateVideo(-1);
            break;
          case 'ArrowRight':
            this.navigateVideo(1);
            break;
        }
      });
    }

    navigateVideo(direction) {
      if (this.currentVideoIndex === null) return;

      const newIndex = this.currentVideoIndex + direction;
      if (newIndex >= 0 && newIndex < this.videos.length) {
        this.closeModal();
        setTimeout(() => this.openModal(newIndex), 100);
      }
    }

    // ==================== 分享功能 ====================

    handleShare() {
      const video = this.videos[this.currentVideoIndex];
      if (!video) return;

      const shareData = {
        title: 'Video',
        text: video.description || '',
        url: `https://www.youtube.com/watch?v=${video.videoId}`
      };

      if (navigator.share) {
        navigator.share(shareData).catch(() => {});
      } else {
        // 降级：复制链接
        navigator.clipboard.writeText(shareData.url).then(() => {
          alert('Link copied to clipboard!');
        });
      }
    }

    // ==================== 响应式处理 ====================

    initResizeListener() {
      const handleResize = debounce(() => {
        const wasDesktop = !this.isMobile;
        this.isMobile = window.innerWidth < 768;

        // 如果从桌面切换到移动端，更新模态框显示
        if (wasDesktop && !this.isMobile && this.modal.classList.contains('active')) {
          const info = this.modal.querySelector('.vg-modal-info');
          if (info) info.classList.remove('mobile-visible');
        }
      }, 250);

      window.addEventListener('resize', handleResize);
    }

    // ==================== 公共方法 ====================

    /**
     * 刷新模块（用于动态更新后）
     */
    refresh() {
      this.initLazyLoad();
      this.initModal();
      this.updateVisibleVideos();
    }

    /**
     * 销毁模块
     */
    destroy() {
      if (this.modal) {
        this.closeModal();
      }
    }
  }

  // ==================== 初始化 ====================

  // 存储实例
  window.VideoGalleryInstances = window.VideoGalleryInstances || {};

  // 初始化所有视频陈列模块
  function initVideoGalleries() {
    const sections = document.querySelectorAll('[data-section-id]');

    sections.forEach((section) => {
      const sectionId = section.dataset.sectionId;
      if (sectionId && !window.VideoGalleryInstances[sectionId]) {
        window.VideoGalleryInstances[sectionId] = new VideoGallery(sectionId);
      }
    });
  }

  // DOM加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoGalleries);
  } else {
    initVideoGalleries();
  }

  // Shopify主题编辑器支持
  if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const sectionId = event.detail.sectionId;
      if (window.VideoGalleryInstances[sectionId]) {
        window.VideoGalleryInstances[sectionId].refresh();
      } else {
        window.VideoGalleryInstances[sectionId] = new VideoGallery(sectionId);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const sectionId = event.detail.sectionId;
      if (window.VideoGalleryInstances[sectionId]) {
        window.VideoGalleryInstances[sectionId].destroy();
        delete window.VideoGalleryInstances[sectionId];
      }
    });

    document.addEventListener('shopify:block:select', (event) => {
      const sectionId = event.detail.sectionId;
      const blockId = event.detail.blockId;
      const instance = window.VideoGalleryInstances[sectionId];

      if (instance) {
        const videoIndex = instance.videos.findIndex((v) => v.id === blockId);
        if (videoIndex !== -1) {
          instance.openModal(videoIndex);
        }
      }
    });
  }

  // 导出类（可选）
  window.VideoGallery = VideoGallery;
})();